import type { ReactNode } from "react";

interface AppHeaderProps {
  /** The app's own name, shown top-left. e.g. "GammaLens", "Daily Implied Move". */
  title: string;
  /**
   * Whole-app controls (ticker/scope dropdown, search-to-jump, or the app's
   * view tabs). Sit immediately right of the title (centre-left).
   */
  primary?: ReactNode;
  /**
   * Panel-only controls that affect just one part of the view (e.g. GammaLens's
   * Front/Next + TCS/TPS/TGS/Zone). Sit centre-right, split from the primary set.
   * Most apps have none.
   */
  secondary?: ReactNode;
  /** The Share button, for apps that support it. Always the last thing on the right. */
  share?: ReactNode;
}

/**
 * The standard 2nd Skies app header. One row, read left-to-right:
 *   [ title ][ primary controls ] ····· [ secondary controls ][ Share ]
 * Freshness lives in the footer (see BrandFooter / AppFreshness), not here.
 *
 * Shared across every Terminal app so the top of each reads as one family.
 * Colours and fonts come from the shared dark palette; nothing here is
 * app-specific.
 */
export function AppHeader({ title, primary, secondary, share }: AppHeaderProps): JSX.Element {
  return (
    <header className="app-header">
      <h1 className="app-header__title">{title}</h1>
      {primary ? <div className="app-header__primary">{primary}</div> : null}
      <div className="app-header__spacer" />
      {secondary ? <div className="app-header__secondary">{secondary}</div> : null}
      {share ? <div className="app-header__utils">{share}</div> : null}
    </header>
  );
}
